// src/pages/UnlabelledTransferList.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector } from "react-redux";
import { TbArrowsSort } from "react-icons/tb";
import { FaExchangeAlt, FaFilter, FaTimesCircle } from "react-icons/fa";
import toast from "react-hot-toast";

import ConfirmDialog from "../components/ConfirmDialog";
import { fmtDate } from "../utils/date";
import {
  useDeleteUnlabelledTransferMutation,
  useListUnlabelledTransfersQuery,
} from "../toolkitServices/unlabelledApi";
import UnlabelledTransferDetailDrawer from "../components/UnlabelledTransferDetailDrawer";

/* ------------ Tiny reusable spinner ------------ */
const Spinner = ({ size = 16, className = "" }) => (
  <span
    className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    style={{ width: size, height: size }}
  />
);

const headerCell =
  "whitespace-nowrap px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wide sm:px-4 sm:text-[11px]";
const dataCell = "whitespace-nowrap px-3 py-2.5 text-xs sm:px-4 sm:text-sm";

const toTitle = (s = "") =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";

const SkeletonRows = ({ cols = 8, rows = 6 }) => (
  <>
    {Array.from({ length: rows }).map((_, r) => (
      <tr key={`sk-${r}`} className="animate-pulse">
        {Array.from({ length: cols }).map((__, c) => (
          <td key={`sk-${r}-${c}`} className={dataCell}>
            <div className="h-3 w-24 bg-slate-200 rounded" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

const tabBtn =
  "inline-flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[10px] font-semibold transition sm:flex-none sm:gap-2 sm:px-3 sm:text-xs";
const tabOn = "border-[#6b59c6] bg-[#6b59c6] text-white shadow-sm";
const tabOff =
  "border-transparent bg-transparent text-slate-600 hover:border-violet-100 hover:bg-white hover:text-[#6b59c6]";

const chipBase =
  "inline-flex items-center gap-2 rounded-full border border-violet-100 bg-white px-2.5 py-1 text-[10px] font-medium text-slate-600";

const UnlabelledTransferList = () => {
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth?.user);

  // ✅ try both employeeId and _id (different apps store differently)
  const myUserId = user?.employeeId || user?._id || user?.id;
  const myUserName = user?.name || user?.userName || "User";

  /* ------------ UI state ------------ */
  const [activeTab, setActiveTab] = useState("incoming"); // incoming | outgoing | all
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [selectedTransfer, setSelectedTransfer] = useState(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // "" | pending | completed | rejected
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  /* ------------ API queries ------------ */
  const [deleteTransfer, { isLoading: deleting }] =
    useDeleteUnlabelledTransferMutation();

  const { data: incomingResp, isFetching: loadingIncoming } =
    useListUnlabelledTransfersQuery(
      { page: 1, limit: 200, toUserId: myUserId },
      { skip: !myUserId }
    );

  const { data: outgoingResp, isFetching: loadingOutgoing } =
    useListUnlabelledTransfersQuery(
      { page: 1, limit: 200, fromUserId: myUserId },
      { skip: !myUserId }
    );

  const { data: allResp, isFetching: loadingAll } =
    useListUnlabelledTransfersQuery({ page: 1, limit: 200 });

  const [deletingTransfer, setDeletingTransfer] = useState(null);

  const pickList = (resp) => {
    // supports {data:[...]}, {data:{data:[...]}}, or raw
    if (!resp) return [];
    if (Array.isArray(resp?.data)) return resp.data;
    if (Array.isArray(resp?.data?.data)) return resp.data.data;
    if (Array.isArray(resp)) return resp;
    return [];
  };

  const getActiveList = () => {
    if (activeTab === "incoming") return pickList(incomingResp);
    if (activeTab === "outgoing") return pickList(outgoingResp);
    return pickList(allResp);
  };

  const transfers = getActiveList();
  const isLoading = loadingIncoming || loadingOutgoing || loadingAll;

  const incomingCount = pickList(incomingResp).length;
  const outgoingCount = pickList(outgoingResp).length;
  const allCount = pickList(allResp).length;

  /* ------------ Ctrl+P shortcut ------------ */
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key.toLowerCase() === "p") {
        e.preventDefault();
        navigate("/unlabelled-transfers/create");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate]);

  /* ------------ sorting ------------ */
  const onSort = (field) => {
    if (sortBy === field) {
      setSortOrder((s) => (s === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const sortIcon = (field) => (
    <span
      className="inline-flex items-center gap-1 cursor-pointer select-none"
      onClick={() => onSort(field)}
      title="Sort"
    >
      <TbArrowsSort className="text-[16px]" />
      {sortBy === field ? (
        <span className="text-[10px]">{sortOrder.toUpperCase()}</span>
      ) : null}
    </span>
  );

  /* ------------ filter + search + sort pipeline ------------ */
  const filteredTransfers = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    return (transfers || []).filter((t) => {
      if (q) {
        const hay = [
          t?._id,
          t?.transferNo,
          t?.fromUserName,
          t?.toUserName,
          t?.status,
          t?.note,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }

      if (statusFilter && String(t?.status || "") !== statusFilter) return false;

      return true;
    });
  }, [transfers, search, statusFilter]);

  const sortedTransfers = useMemo(() => {
    const list = [...(filteredTransfers || [])];

    const getVal = (t) => {
      switch (sortBy) {
        case "_id":
          return String(t?._id || "");
        case "fromUserName":
          return String(t?.fromUserName || "").toLowerCase();
        case "toUserName":
          return String(t?.toUserName || "").toLowerCase();
        case "status":
          return String(t?.status || "").toLowerCase();
        case "createdAt":
        default:
          return t?.createdAt ? new Date(t.createdAt).getTime() : 0;
      }
    };

    list.sort((a, b) => {
      const av = getVal(a);
      const bv = getVal(b);
      if (av < bv) return sortOrder === "asc" ? -1 : 1;
      if (av > bv) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return list;
  }, [filteredTransfers, sortBy, sortOrder]);

  /* ------------ chips ------------ */
  const chips = useMemo(() => {
    const arr = [];
    if (search) arr.push({ key: "search", label: `Search: ${search}` });
    if (statusFilter)
      arr.push({ key: "status", label: `Status: ${toTitle(statusFilter)}` });
    arr.push({
      key: "tab",
      label:
        activeTab === "incoming"
          ? "Tab: Incoming"
          : activeTab === "outgoing"
          ? "Tab: Outgoing"
          : "Tab: All",
    });
    return arr;
  }, [search, statusFilter, activeTab]);

  const clearChip = (k) => {
    if (k === "search") setSearch("");
    if (k === "status") setStatusFilter("");
  };

  const clearAllChips = () => {
    setSearch("");
    setStatusFilter("");
  };

  const showingCount = sortedTransfers?.length || 0;

  return (
    <div className="relative flex h-[calc(100vh-140px)] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg md:h-[calc(100vh-70px)]">
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between gap-3 bg-gradient-to-r from-[#6b59c6] via-[#806dcc] to-[#9481ce] px-3 py-2.5 text-white shadow-sm sm:px-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/20">
            <FaExchangeAlt className="text-sm" />
          </span>
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold tracking-tight sm:text-lg">
              Unlabelled Transfers
            </h2>
            <p className="truncate text-[9px] text-violet-100 sm:text-[11px]">
              Incoming and outgoing unlabelled stock movement
            </p>
          </div>
          {isLoading && (
            <span className="hidden items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px] sm:inline-flex">
              <Spinner size={10} /> refreshing…
            </span>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <span className="hidden rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-medium ring-1 ring-white/10 lg:inline-flex">
            {showingCount} shown
          </span>
          <button
            type="button"
            onClick={() => setIsFilterOpen(true)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white/15 text-white ring-1 ring-white/20 transition hover:bg-white/25 sm:hidden"
            title="Filters"
            aria-label="Open filters"
          >
            <FaFilter className="text-white" />
          </button>

          <Link
            to="/transfers/unlabelled-transfers/create"
            className="inline-flex h-9 items-center gap-1 rounded-xl bg-white px-3 text-[11px] font-semibold text-[#6b4fd8] shadow-sm transition hover:bg-violet-50 sm:rounded-full sm:px-4"
          >
            + Add New
          </Link>
        </div>
      </div>

      {/* Transfer direction */}
      <div className="shrink-0 border-b bg-slate-50/80 px-2.5 py-2 sm:px-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex w-full items-center gap-1 rounded-xl border border-slate-200 bg-slate-100 p-1 sm:w-auto">
            <button
              type="button"
              onClick={() => setActiveTab("incoming")}
              className={`${tabBtn} ${activeTab === "incoming" ? tabOn : tabOff}`}
            >
              Incoming
              <span className="inline-flex min-w-[20px] justify-center rounded-full bg-white/90 text-[10px] text-[#6b4fd8] px-1">
                {incomingCount}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("outgoing")}
              className={`${tabBtn} ${activeTab === "outgoing" ? tabOn : tabOff}`}
            >
              Outgoing
              <span className="inline-flex min-w-[20px] justify-center rounded-full bg-white/90 text-[10px] text-[#6b4fd8] px-1">
                {outgoingCount}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("all")}
              className={`${tabBtn} ${activeTab === "all" ? tabOn : tabOff}`}
            >
              All
              <span className="inline-flex min-w-[20px] justify-center rounded-full bg-white/90 text-[10px] text-[#6b4fd8] px-1">
                {allCount}
              </span>
            </button>
          </div>

          <div className="hidden flex-col items-end text-[11px] text-slate-500 md:flex">
            <span className="font-medium">
              {isLoading ? "Loading transfers..." : `Showing ${showingCount} transfers`}
            </span>
            <span className="text-[10px]">
              Sorted by{" "}
              <span className="font-semibold">
                {sortBy === "createdAt"
                  ? "Created On"
                  : sortBy === "_id"
                  ? "Transfer ID"
                  : toTitle(sortBy)}
              </span>{" "}
              ({sortOrder.toUpperCase()})
            </span>
          </div>
        </div>
      </div>

      {/* mobile search row */}
      <div className="border-b bg-slate-50/80 px-3 py-1.5 sm:hidden">
        <div className="flex items-center">
          <input
            type="text"
            placeholder="Search transfer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 px-3 py-2 rounded-xl text-[11px] text-gray-800 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-violet-400 bg-white"
          />
        </div>
      </div>

      {/* desktop top filters */}
      <div className="hidden sm:block px-4 py-2 border-b bg-slate-50/80">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-[1px] w-[160px]">
            <label className="text-[11px] font-medium text-slate-600">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-2.5 py-1.5 rounded-lg text-[11px] text-gray-800 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-violet-400"
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <div className="flex flex-col gap-[1px] flex-1 min-w-[160px]">
            <label className="text-[11px] font-medium text-slate-600">Search</label>
            <input
              type="text"
              placeholder="Transfer ID / user…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="px-3 py-1.5 rounded-lg text-[11px] text-gray-800 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-violet-400"
            />
          </div>

          <div className="flex flex-col gap-[1px] w-auto">
            <label className="text-[11px] font-medium text-slate-600 invisible">
              Filters
            </label>
            <button
              type="button"
              onClick={() => setIsFilterOpen(true)}
              className="inline-flex items-center gap-1 bg-white text-[#6b59c6] px-3 py-1.5 rounded-full text-[11px] border border-violet-200 hover:bg-violet-50 outline-[#6b59c6]"
              title="Advanced filters"
            >
              <FaFilter /> Filters
            </button>
          </div>
        </div>
      </div>

      {/* chips */}
      {chips.length > 0 && (
        <div className="px-3 sm:px-4 py-1.5 sm:py-2 border-b bg-slate-50">
          <div className="flex items-center gap-2 overflow-x-auto whitespace-nowrap">
            <div className="flex items-center gap-2 flex-nowrap">
              {chips
                .filter((c) => c.key !== "tab")
                .map((c) => (
                  <span key={c.key} className={`${chipBase} shrink-0`}>
                    {c.label}
                    <button
                      className="text-slate-500 hover:text-red-500"
                      onClick={() => clearChip(c.key)}
                      title="Clear"
                    >
                      <FaTimesCircle />
                    </button>
                  </span>
                ))}

              <span className={`${chipBase} bg-[#f4f0ff] border-[#e6e1f5] shrink-0`}>
                {chips.find((x) => x.key === "tab")?.label}
              </span>
            </div>

            <button
              className="ml-auto text-[11px] text-[#6b59c6] hover:underline shrink-0"
              onClick={clearAllChips}
              title="Clear all"
            >
              Clear all
            </button>
          </div>
        </div>
      )}

      {/* table */}
      <div className="relative min-h-0 flex-1 overflow-x-auto overflow-y-auto overscroll-x-contain bg-white [-webkit-overflow-scrolling:touch]">
        <table className="w-full min-w-[900px] text-left text-xs sm:text-sm">
          <thead className="sticky top-0 z-10 bg-[#efeafd] text-[#4b3d91] shadow-sm">
            <tr>
              <th className={headerCell}>Transfer No</th>
              <th className={headerCell}>
                From User {sortIcon("fromUserName")}
              </th>
              <th className={headerCell}>
                To User {sortIcon("toUserName")}
              </th>
              <th className={`${headerCell} text-center`}>Items</th>
              <th className={headerCell}>Status {sortIcon("status")}</th>
              <th className={headerCell}>Created {sortIcon("createdAt")}</th>
              <th className={`${headerCell} text-center`}>Action</th>
            </tr>
          </thead>

          <tbody className="bg-white text-gray-700">
            <AnimatePresence initial={false}>
              {isLoading ? (
                <SkeletonRows cols={7} rows={6} />
              ) : sortedTransfers.length ? (
                sortedTransfers.map((tr, idx) => (
                  <motion.tr
                    key={tr._id || idx}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2, delay: idx * 0.02 }}
                    className="border-t hover:bg-[#f4f0ff] transition"
                  >
                    <td className={`${dataCell} font-mono text-[11px] sm:text-xs`}>
                      {tr.transferNo || tr._id || "-"}
                    </td>

                    <td className={dataCell}>{tr.fromUserName || "-"}</td>
                    <td className={dataCell}>{tr.toUserName || "-"}</td>

                    <td className={`${dataCell} text-center`}>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="px-1.5 py-0.5 rounded-full bg-slate-50 text-[10px] text-slate-700 font-medium">
                          {tr?.summary?.total ?? tr?.items?.length ?? tr?.totalItems ?? 0}
                        </span>
                      </span>
                    </td>

                    <td className={dataCell}>
                      <span
                        className={`inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-semibold tracking-wide uppercase border ${
                          tr.status === "pending"
                            ? "bg-amber-50 text-amber-700 border-amber-200"
                            : tr.status === "completed"
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : tr.status === "rejected"
                            ? "bg-rose-50 text-rose-700 border-rose-200"
                            : "bg-violet-50 text-violet-700 border-violet-200"
                        }`}
                      >
                        {toTitle(tr.status || "pending")}
                      </span>
                    </td>

                    <td className={dataCell}>{fmtDate(tr.createdAt)}</td>

                    <td className={`${dataCell} text-center`}>
                      <div className="inline-flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => setSelectedTransfer(tr)}
                          className="inline-flex items-center justify-center px-3 py-1.5 rounded-full bg-[#6b59c6]/8 text-[#6b59c6] hover:bg-[#6b59c6]/15 text-xs font-medium border border-[#6b59c6]/20 transition-colors"
                        >
                          {activeTab === "incoming" && tr.status === "pending" ? "Review" : "View"}
                        </button>

                        {/* outgoing + pending -> allow delete */}
                        {activeTab === "outgoing" && tr.status === "pending" && (
                          <button
                            type="button"
                            onClick={() => setDeletingTransfer(tr)}
                            className="inline-flex items-center justify-center px-3 py-1.5 rounded-full bg-rose-50 text-rose-700 hover:bg-rose-100 text-xs font-medium border border-rose-200 transition-colors"
                            disabled={deleting}
                            title="Delete transfer"
                          >
                            {deleting ? "Deleting..." : "Delete"}
                          </button>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))
              ) : (
                <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <td colSpan={7} className="px-4 py-8 text-center text-xs text-slate-500">
                    No transfers found. Try changing filters or create a new transfer.
                  </td>
                </motion.tr>
              )}
            </AnimatePresence>
          </tbody>
        </table>
      </div>

      {/* filter modal */}
      <AnimatePresence>
        {isFilterOpen && (
          <motion.div
            className="fixed inset-0 z-[9998] flex items-end justify-center bg-slate-950/40 p-3 backdrop-blur-[1px] sm:items-center sm:p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsFilterOpen(false)}
          >
            <motion.div
              className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
              initial={{ scale: 0.9, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 20, opacity: 0 }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-center justify-between bg-gradient-to-r from-[#6b59c6] to-[#9481ce] px-4 py-3 text-white">
                <div>
                  <h3 className="text-sm font-semibold">
                    Transfer Filters
                  </h3>
                  <p className="mt-0.5 text-[10px] text-violet-100">
                    Filter unlabelled transfers by status and search.
                  </p>
                </div>
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/15 transition hover:bg-white/25"
                  onClick={() => setIsFilterOpen(false)}
                  aria-label="Close filters"
                >
                  <FaTimesCircle />
                </button>
              </div>

              <div className="space-y-4 overflow-y-auto p-4 text-[11px]">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-[10px] font-medium text-slate-600">Status</label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-[11px] text-slate-700 outline-none focus:border-violet-300 focus:ring-1 focus:ring-violet-100"
                    >
                      <option value="">All</option>
                      <option value="pending">Pending</option>
                      <option value="completed">Completed</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-[10px] font-medium text-slate-600">Search</label>
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Transfer ID / user…"
                      className="h-10 w-full rounded-xl border border-slate-200 px-3 text-[11px] text-slate-700 outline-none focus:border-violet-300 focus:ring-1 focus:ring-violet-100"
                    />
                  </div>
                </div>

                <div className="text-[11px] text-slate-500">
                  Tip: Ctrl + P to create a new transfer quickly.
                </div>
              </div>

              <div className="flex items-center justify-between border-t bg-slate-50 px-4 py-3">
                <button
                  type="button"
                  onClick={() => {
                    setSearch("");
                    setStatusFilter("");
                    toast.success("Filters reset");
                  }}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-700"
                >
                  Reset
                </button>

                <button
                  type="button"
                  onClick={() => setIsFilterOpen(false)}
                  className="rounded-full bg-[#6b59c6] px-5 py-2 text-[11px] font-semibold text-white shadow-sm transition hover:bg-[#5948ae]"
                >
                  Done
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* delete confirm */}
      <ConfirmDialog
        open={!!deletingTransfer}
        title="Delete transfer?"
        message={
          deletingTransfer
            ? `Transfer ${
                deletingTransfer.transferNo || deletingTransfer._id
              } will be deleted.`
            : ""
        }
        confirmText={deleting ? "Deleting..." : "Delete"}
        cancelText="Cancel"
        loading={deleting}
        destructive
        onCancel={() => {
          if (!deleting) setDeletingTransfer(null);
        }}
        onConfirm={async () => {
          if (!deletingTransfer) return;

          try {
            await deleteTransfer(deletingTransfer._id).unwrap();
            toast.success("Transfer deleted");
            setDeletingTransfer(null);
          } catch (err) {
            toast.error(err?.data?.message || "Failed to delete transfer");
          }
        }}
      />

      {/* Drawer */}
      {selectedTransfer && (
        <UnlabelledTransferDetailDrawer
          transfer={selectedTransfer}
          onClose={() => setSelectedTransfer(null)}
          userId={myUserId}
          userName={myUserName}
          canRespond={activeTab === "incoming"} // incoming => respond, outgoing/all => read-only
        />
      )}
    </div>
  );
};

export default UnlabelledTransferList;
